import { useCallback, useRef, useState, type ReactNode } from "react";
import SelectOrgDialog from "./SelectOrgDialog";
import { useSelectOrgDialogViewModel } from "./SelectOrgDialog.ViewModel";
import type { SelectOrgTarget } from "./SelectOrgDialog.ViewModel.types";

export interface SelectOrgDialogController {
  /**
   * Prompts for an environment and resolves with the auth URL for the second,
   * environment-scoped OAuth leg — or null if the user cancelled or it failed.
   */
  selectOrg: (target: SelectOrgTarget) => Promise<string | null>;
  selectOrgDialog: ReactNode;
}

export const useSelectOrgDialog = (): SelectOrgDialogController => {
  const [target, setTarget] = useState<SelectOrgTarget | null>(null);
  const resolverRef = useRef<((value: string | null) => void) | null>(null);

  const resolve = useCallback((value: string | null) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setTarget(null);
  }, []);

  const selectOrg = useCallback(
    (next: SelectOrgTarget): Promise<string | null> => {
      setTarget(next);
      return new Promise<string | null>((resolvePromise) => {
        resolverRef.current = resolvePromise;
      });
    },
    [],
  );

  const onAuthUrl = useCallback(
    (authUrl: string) => resolve(authUrl),
    [resolve],
  );
  const onCancel = useCallback(() => resolve(null), [resolve]);

  const vm = useSelectOrgDialogViewModel(target, onAuthUrl, onCancel);

  return {
    selectOrg,
    selectOrgDialog: <SelectOrgDialog target={target} vm={vm} />,
  };
};
